import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { ListPage } from '../../components/ListPage';
import type { Column } from '../../components/ListPage';
import { RecordActions } from '../../components/RecordActions';
import { RECORD_FIELDS } from '../../components/recordFields';
import { KeyValue, Pill, Spinner, FormGrid, Field, TextInput, NumberInput, LineTable } from '../../components/ui';
import { SearchSelect } from '../../components/SearchSelect';
import { QcReviewEditor } from './QcReviewEditor';
import { BomInputsEditor } from './BomInputsEditor';
import { useDetail } from '../../lib/useDetail';
import { api, ApiError } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import { can } from '../../lib/permissions';
import { num, planStatus } from '../../lib/format';
import type {
  Bom, BomLine, BomOperation, Item, Operation,
  BomConversionResult, BomConversionOperation, BomConversionLine, BomConversionAnomaly,
  QcReviewQueue, QcReviewQueueBom,
} from '../../lib/types';

// 核心服务写接口返回 { data: payload } 信封；防御性解包，兼容个别直接返回 payload 的历史写法。
function unwrap<T>(body: unknown): T {
  if (body && typeof body === 'object' && 'data' in (body as Record<string, unknown>)) {
    return (body as { data: T }).data;
  }
  return body as T;
}

const ACTION_LABEL: Record<string, string> = { created: '新建', updated: '已更新', skipped: '已跳过·保留' };

/** 决策二：BOM 还有未判定工序时不能批准（后端 transitionBomStatus 会 409），这里提前告知原因。 */
function hasPendingReview(ops: BomOperation[]): boolean {
  return ops.some((o) => (o.qc_review_status ?? 'pending') === 'pending'
    || ((o.qc_review_status ?? 'pending') === 'required' && !o.qc_checkpoint_id));
}

// —— P6-6：把 ERPNext BOM 一键转换为工艺方案（模板 + 材料方案）草稿。
//     先 preview 只读推导、不落库；人工核对无误后 commit 生成草稿（须再批准才能用于新工单）。
//     BOM 原件与历史工单均不被修改；误标 USD 工序见异常表，本轮不乘汇率。——
function BomConvertPanel({ bomId, canConvert }: { bomId: string; canConvert: boolean }) {
  const [preview, setPreview] = useState<BomConversionResult | null>(null);
  const [committed, setCommitted] = useState<BomConversionResult | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const run = async (mode: 'preview' | 'commit') => {
    setErr(null); setBusy(true);
    try {
      const res = unwrap<BomConversionResult>(
        await api.post('/api/process-plans/convert-from-bom', { bom_id: bomId, mode }),
      );
      if (mode === 'preview') setPreview(res); else setCommitted(res);
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : '转换失败');
    } finally { setBusy(false); }
  };

  const result = committed ?? preview;
  if (!result) {
    return (
      <div style={{ marginTop: 12 }}>
        {err ? <div className="notice bad" style={{ marginBottom: 8 }}>{err}</div> : null}
        <div className="detail-actions">
          <button className="btn" onClick={() => run('preview')} disabled={busy}>{busy ? '推导中…' : '预览转换为工艺方案'}</button>
          <span className="muted" style={{ fontSize: 12, alignSelf: 'center' }}>只读推导，不写库；核对后再据草稿生成（P6-6）</span>
        </div>
      </div>
    );
  }

  const t = result.template;
  const mp = result.material_plan;
  const anomalies = result.anomalies ?? [];
  return (
    <div style={{ marginTop: 12 }} onClick={(e) => e.stopPropagation()}>
      <div className="notice" style={{ marginBottom: 8 }}>
        <strong>转换预览</strong> · BOM {result.bom.number} → 工艺模板 {t.code}
        {mp ? ` + 材料方案 ${mp.code}` : '（无计入物料，不建材料方案）'}
        {' '}· 分类：{result.classification}
      </div>

      <div className="kv-grid" style={{ marginBottom: 8 }}>
        <KeyValue k="模板工序数">{num(t.operation_count, 0)}</KeyValue>
        <KeyValue k="材料行数">{mp ? num(mp.line_count, 0) : '—'}</KeyValue>
      </div>

      <div className="k" style={{ fontSize: 11, color: 'var(--ink-3)', margin: '4px 0' }}>工艺模板工序（仅记录工时，费用由后续财务模块核算）</div>
      <LineTable<BomConversionOperation>
        rows={t.operations}
        emptyText="无工序"
        columns={[
          { header: '序', align: 'right', render: (o) => <span className="num">{o.sort_order + 1}</span> },
          { header: '工序', render: (o) => `${o.operation_code ?? ''} ${o.operation_name ?? ''}`.trim() || '—' },
          { header: '工位', render: (o) => o.workstation || '—' },
          { header: '工时(分)', align: 'right', render: (o) => <span className="num">{num(o.planned_minutes, 0)}</span> },
          // 决策二：ERPNext 源系统的 qc_required 只是「源上怎么标的」，不是质量的审核结论。
          // 转换生成的模板工序一律 pending，必须由质量逐道判定后才能批准。
          { header: '质检', render: (o) => (
            <span style={{ display: 'inline-flex', gap: 4, alignItems: 'center' }}>
              <Pill tone="neutral" label="待质量审核" />
              {o.qc_required ? <span className="muted">源标:需检</span> : null}
            </span>
          ) },
          { header: '币种', render: (o) => o.currency_suspect ? <Pill tone="bad" label="误标·待核" /> : <span className="muted">ARS</span> },
        ]}
      />

      {mp ? (
        <>
          <div className="k" style={{ fontSize: 11, color: 'var(--ink-3)', margin: '10px 0 4px' }}>材料方案行（损耗/批次默认 0，批准前人工补齐）</div>
          <LineTable<BomConversionLine>
            rows={mp.lines}
            emptyText="无物料"
            columns={[
              { header: '物料', render: (l) => `${l.item_code ?? ''} ${l.item_name_zh ?? ''}`.trim() || '—' },
              { header: '单方用量', align: 'right', render: (l) => <span className="num">{num(l.quantity, 3)}</span> },
              { header: '单位', render: (l) => <span className="muted">{l.uom}</span> },
              { header: '损耗率', align: 'right', render: (l) => <span className="num">{num(l.loss_rate * 100, 1)}%</span> },
              { header: '批次', render: (l) => l.batch_required ? <Pill tone="warn" label="需批次" /> : <span className="muted">—</span> },
              { header: '备注', render: (l) => <span className="muted">{l.notes || '—'}</span> },
            ]}
          />
        </>
      ) : null}

      {anomalies.length ? (
        <>
          <div className="k" style={{ fontSize: 11, color: 'var(--ink-3)', margin: '10px 0 4px' }}>
            异常 · 疑似误标币种工序（{anomalies.length}）· 保留原值不乘汇率，待人工确认
          </div>
          <LineTable<BomConversionAnomaly>
            rows={anomalies}
            columns={[
              { header: 'BOM', render: (a) => <span className="num">{a.bom_number}</span> },
              { header: '工序', render: (a) => `${a.operation_code ?? ''} ${a.operation_name ?? ''}`.trim() || '—' },
              { header: '原币种', render: (a) => <Pill tone="bad" label={a.original_currency} /> },
              { header: '建议币种', render: (a) => <span className="muted">{a.suggested_currency}</span> },
              { header: '确认', render: (a) => a.confirmed ? <Pill tone="ok" label="已确认" /> : <Pill tone="neutral" label="待核" /> },
            ]}
          />
        </>
      ) : null}

      {committed ? (
        <div className="notice ok" style={{ margin: '10px 0 4px' }}>
          {committed.note}
          <div style={{ marginTop: 6 }}>
            模板：<strong>{committed.template.code}</strong> <Pill tone={planStatus(committed.template.status).tone} label={ACTION_LABEL[committed.template.action ?? ''] ?? planStatus(committed.template.status).label} />
            {mp ? <span style={{ marginLeft: 10 }}>方案：<strong>{committed.material_plan?.code}</strong> <Pill tone={planStatus(committed.material_plan?.status ?? 'draft').tone} label={ACTION_LABEL[committed.material_plan?.action ?? ''] ?? planStatus(committed.material_plan?.status ?? 'draft').label} /></span> : null}
          </div>
          <div style={{ marginTop: 6, display: 'flex', gap: 12 }}>
            <Link to="/process-templates" className="btn link">前往工艺模板批准 →</Link>
            {committed.material_plan ? <Link to="/material-plans" className="btn link">前往材料方案批准 →</Link> : null}
          </div>
        </div>
      ) : null}

      {err ? <div className="notice bad" style={{ margin: '8px 0' }}>{err}</div> : null}
      <div className="detail-actions">
        {canConvert && !committed ? (
          <button className="btn primary" onClick={() => run('commit')} disabled={busy}>{busy ? '生成中…' : '确认为草稿（不改动 BOM / 历史工单）'}</button>
        ) : null}
        {!committed ? <button className="btn" onClick={() => { setPreview(null); setErr(null); }} disabled={busy}>取消预览</button> : null}
        {canConvert && committed ? <button className="btn" onClick={() => run('commit')} disabled={busy}>重新生成草稿</button> : null}
      </div>
    </div>
  );
}

// —— BOM 详情：列表接口不含用料/工序，展开时按 id 拉 GET /api/boms/:id ——
function BomDetail({ id, canConvert, onApproved }: { id: string; canConvert: boolean; onApproved: () => void }) {
  const { data, loading, error, reload } = useDetail<Bom>(`/api/boms/${id}`);
  const { user } = useAuth();
  const [approving, setApproving] = useState(false);
  const [approvalError, setApprovalError] = useState<string | null>(null);
  const [makeDefault, setMakeDefault] = useState<boolean | null>(null);
  async function approve() {
    if (!data || approving) return;
    if (!window.confirm(`批准 BOM ${data.number}？请先核对用料、工序及质检审核。${(makeDefault ?? Boolean(data.is_default)) ? '本 BOM 将作为该产品默认 BOM。' : ''}`)) return;
    setApproving(true); setApprovalError(null);
    try {
      await api.post(`/api/boms/${id}/status`, { status: 'approved', is_default: makeDefault ?? Boolean(data.is_default) });
      reload(); onApproved();
    } catch (e) { setApprovalError(e instanceof Error ? e.message : '批准失败'); }
    finally { setApproving(false); }
  }
  if (loading && !data) return <div style={{ padding: 10 }}><Spinner label="加载 BOM 明细…" /></div>;
  if (error) return <div className="notice bad" style={{ margin: 10 }}>{error}</div>;
  const lines = data?.lines ?? [];
  const ops = data?.operations ?? [];
  return (
    <div className="detail" onClick={(e) => e.stopPropagation()}>
      <div className="detail-head">
        <span className="id">{data?.number}</span>
        <h3>{data ? `${data.item_code ?? ''} ${data.item_name_zh ?? ''}`.trim() : ''}</h3>
        {data ? <Pill tone={data.active ? 'ok' : 'neutral'} label={data.active ? '启用' : '停用'} /> : null}
        {data ? <Pill tone={data.status === 'approved' ? 'ok' : 'warn'} label={data.status === 'approved' ? '已批准' : data.status === 'deactivated' ? '已停用' : '草稿 · 未批准'} /> : null}
      </div>
      <div className="kv-grid" style={{ marginBottom: 8 }}>
        <KeyValue k="成品数量">{num(data?.quantity, 3)}</KeyValue>
        <KeyValue k="版本">{data?.version ?? '—'}</KeyValue>
        <KeyValue k="备注">{data?.notes || '—'}</KeyValue>
      </div>
      <div className="k" style={{ fontSize: 11, color: 'var(--ink-3)', margin: '4px 0' }}>用料（{lines.length}）</div>
      <LineTable<BomLine>
        rows={lines}
        emptyText="暂无用料"
        columns={[
          { header: '物料', render: (c) => `${c.item_code ?? ''} ${c.item_name_zh ?? ''}`.trim() || '—' },
          { header: '数量', align: 'right', render: (c) => <span className="num">{num(c.quantity, 3)}</span> },
          { header: '单位', render: (c) => <span className="muted">{c.uom}</span> },
          { header: '关联工序', render: (c) => <span className="muted">{c.operation_name || '—'}</span> },
        ]}
      />
      <div className="k" style={{ fontSize: 11, color: 'var(--ink-3)', margin: '10px 0 4px' }}>工序（{ops.length}）· 工单下达时据此展开为工序任务</div>
      <LineTable<BomOperation>
        rows={ops}
        emptyText="暂无工序"
        columns={[
          { header: '工序', render: (o) => `${o.operation_code ?? ''} ${o.operation_name ?? ''}`.trim() || '—' },
          { header: '工位', render: (o) => o.workstation || '—' },
          { header: '工时(分)', align: 'right', render: (o) => <span className="num">{num(o.time_minutes, 0)}</span> },
          // 决策二：质检列展示三态审核结论 + 审核依据；质量角色可直接在此判定
          { header: '质检审核', render: (o) => <QcReviewEditor bomId={id} op={o} onSaved={reload} /> },
        ]}
      />
      {hasPendingReview(ops) ? (
        <div className="notice warn" style={{ margin: '8px 0' }}>
          还有工序未完成质检审核：未审核 ≠ 已确认免检。BOM 存在 pending 工序时无法批准，因此不能开工。
        </div>
      ) : null}
      {approvalError ? <div className="notice bad" role="alert">{approvalError}</div> : null}
      {data && can(user, 'boms', 'transition') ? <BomInputsEditor bom={data} onSaved={reload} /> : null}
      {data?.status === 'draft' && can(user, 'boms', 'transition') ? (
        <div className="panel-body">
          <p>启用与批准是两个状态。核对用料和工序，并完成下方各工序质检审核后，才能批准用于订单开工。</p>
          <label><input type="checkbox" checked={makeDefault ?? Boolean(data.is_default)} onChange={(e) => setMakeDefault(e.target.checked)} disabled={approving} /> 设为该产品默认 BOM（替换原默认选择）</label>
          <button className="btn primary" onClick={approve} disabled={approving || hasPendingReview(ops)}>{approving ? '正在批准…' : '批准 BOM'}</button>
        </div>
      ) : null}
      {canConvert ? <BomConvertPanel bomId={id} canConvert={canConvert} /> : null}
    </div>
  );
}

interface LineRow { itemId: string; label: string; quantity: string; uom: string; operationName: string; }
interface OpRow { operationId: string; label: string; workstation: string; timeMinutes: string; hourlyCost: string; qcRequired: boolean; }

function NewBomForm({ onCreated, onCancel }: { onCreated: () => void; onCancel: () => void }) {
  const [itemId, setItemId] = useState<string | null>(null);
  const [itemLabel, setItemLabel] = useState('');
  const [quantity, setQuantity] = useState('1');
  const [version, setVersion] = useState('1');
  const [notes, setNotes] = useState('');
  const [lines, setLines] = useState<LineRow[]>([{ itemId: '', label: '', quantity: '1', uom: 'Unidad', operationName: '' }]);
  const [ops, setOps] = useState<OpRow[]>([]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const setLine = (i: number, patch: Partial<LineRow>) => setLines((ls) => ls.map((l, j) => (j === i ? { ...l, ...patch } : l)));
  const setOp = (i: number, patch: Partial<OpRow>) => setOps((os) => os.map((o, j) => (j === i ? { ...o, ...patch } : o)));

  const submit = async () => {
    setErr(null);
    if (!itemId) { setErr('请选择成品物料'); return; }
    const validLines = lines.filter((l) => l.itemId && Number(l.quantity) > 0);
    if (!validLines.length) { setErr('至少需要一种有效原材料（选择物料且数量>0）'); return; }
    setBusy(true);
    try {
      await api.post('/api/boms', {
        item_id: itemId,
        quantity: Number(quantity || 1),
        version: Number(version || 1),
        notes: notes || null,
        lines: validLines.map((l) => ({ item_id: l.itemId, quantity: Number(l.quantity), uom: l.uom || 'Unidad', operation_name: l.operationName || null })),
        operations: ops.filter((o) => o.operationId).map((o) => {
          const body: Record<string, unknown> = { operation_id: o.operationId };
          if (o.workstation) body.workstation = o.workstation;
          if (o.timeMinutes !== '') body.time_minutes = Number(o.timeMinutes);
          if (o.hourlyCost !== '') body.hourly_cost = Number(o.hourlyCost);
          // 决策二：这里【不】下发 qc_required。
          // 「是否需检」是质量的三态审核结论，生产建 BOM 时无权判定；
          // 工序建出来就是 pending，等质量在 BOM 详情里逐道审核后才能批准开工。
          return body;
        }),
      });
      onCreated();
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : '创建失败');
    } finally { setBusy(false); }
  };

  return (
    <section className="panel" style={{ marginBottom: 'var(--gap)' }}>
      <div className="panel-head"><div><h2>新建 BOM</h2><div className="desc">写入核心服务 /api/boms · 定义成品、用料与工艺路线（工序），供工单下达时展开工序任务、领料时按用量比例计算</div></div></div>
      <div className="panel-body">
        {err ? <div className="notice bad" style={{ marginBottom: 12 }}>{err}</div> : null}
        <FormGrid>
          <Field label="成品物料" required>
            <SearchSelect<Item>
              endpoint="/api/items" value={itemId} label={itemLabel}
              onChange={(row) => { setItemId(row?.id ?? null); setItemLabel(row ? `${row.code} ${row.name_zh}` : ''); }}
              optionTitle={(it) => `${it.code} · ${it.name_zh}`} optionMeta={(it) => it.name_es || undefined} placeholder="检索成品物料…"
            />
          </Field>
          <Field label="成品数量"><NumberInput value={quantity} onChange={(e) => setQuantity(e.target.value)} /></Field>
          <Field label="版本"><NumberInput value={version} onChange={(e) => setVersion(e.target.value)} /></Field>
          <Field label="备注" span><TextInput value={notes} onChange={(e) => setNotes(e.target.value)} /></Field>
        </FormGrid>

        <div className="detail-head" style={{ margin: '14px 0 6px' }}><h3 style={{ fontSize: 14 }}>用料明细</h3><div className="spacer" />
          <button className="btn link" onClick={() => setLines((ls) => [...ls, { itemId: '', label: '', quantity: '1', uom: 'Unidad', operationName: '' }])}>+ 添加用料</button>
        </div>
        {lines.map((l, i) => (
          <div key={i} className="form-row" style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 1.4fr auto', gap: 8, marginBottom: 6, alignItems: 'end' }}>
            <Field label={`原材料 ${i + 1}`}>
              <SearchSelect<Item> endpoint="/api/items" value={l.itemId || null} label={l.label}
                onChange={(row) => setLine(i, { itemId: row?.id ?? '', label: row ? `${row.code} ${row.name_zh}` : '' })}
                optionTitle={(it) => `${it.code} · ${it.name_zh}`} placeholder="检索物料…" />
            </Field>
            <Field label="数量"><NumberInput value={l.quantity} onChange={(e) => setLine(i, { quantity: e.target.value })} /></Field>
            <Field label="单位"><TextInput value={l.uom} onChange={(e) => setLine(i, { uom: e.target.value })} /></Field>
            <Field label="关联工序(选填)"><TextInput value={l.operationName} onChange={(e) => setLine(i, { operationName: e.target.value })} /></Field>
            <button className="btn" onClick={() => setLines((ls) => ls.filter((_, j) => j !== i))} title="删除该用料">×</button>
          </div>
        ))}

        <div className="detail-head" style={{ margin: '14px 0 6px' }}><h3 style={{ fontSize: 14 }}>工艺路线（工序）</h3><div className="spacer" />
          <button className="btn link" onClick={() => setOps((os) => [...os, { operationId: '', label: '', workstation: '', timeMinutes: '', hourlyCost: '', qcRequired: false }])}>+ 添加工序</button>
        </div>
        {ops.map((o, i) => (
          <div key={i} className="form-row" style={{ display: 'grid', gridTemplateColumns: '2fr 1.2fr 1fr 1fr auto auto', gap: 8, marginBottom: 6, alignItems: 'end' }}>
            <Field label={`工序 ${i + 1}`}>
              <SearchSelect<Operation> endpoint="/api/operations" value={o.operationId || null} label={o.label}
                onChange={(row) => setOp(i, { operationId: row?.id ?? '', label: row ? `${row.code} ${row.name_zh}` : '', workstation: row?.workstation ?? o.workstation, timeMinutes: row ? String(row.default_minutes ?? '') : o.timeMinutes, hourlyCost: row ? String(row.hourly_cost ?? '') : o.hourlyCost, qcRequired: row ? !!row.qc_required : o.qcRequired })}
                optionTitle={(it) => `${it.code} · ${it.name_zh}`} placeholder="检索工序…" />
            </Field>
            <Field label="工位"><TextInput value={o.workstation} onChange={(e) => setOp(i, { workstation: e.target.value })} /></Field>
            <Field label="工时(分)"><NumberInput value={o.timeMinutes} onChange={(e) => setOp(i, { timeMinutes: e.target.value })} /></Field>
            <label style={{ display: 'flex', gap: 5, alignItems: 'center', fontSize: 12.5, paddingBottom: 8 }}>
              <input type="checkbox" checked={o.qcRequired} onChange={(e) => setOp(i, { qcRequired: e.target.checked })} />需质检
            </label>
            <button className="btn" onClick={() => setOps((os) => os.filter((_, j) => j !== i))} title="删除该工序">×</button>
          </div>
        ))}

        <div className="detail-actions">
          <button className="btn primary" onClick={submit} disabled={busy}>{busy ? '提交中…' : '创建 BOM'}</button>
          <button className="btn" onClick={onCancel} disabled={busy}>取消</button>
        </div>
      </div>
    </section>
  );
}

export function BomsPage() {
  const { user } = useAuth();
  const [reloadSignal, setReloadSignal] = useState(0);
  const [creating, setCreating] = useState(false);
  const canWrite = can(user, 'boms', 'create');
  // 审核队列点「去审核」会带 ?open=<bomId> 跳过来，直接展开那一行
  const [searchParams] = useSearchParams();
  const openId = searchParams.get('open');

  const columns: Column<Bom>[] = [
    { header: 'BOM 编号', render: (b) => <span className="num strong">{b.number}</span> },
    { header: '成品', render: (b) => `${b.item_code ?? ''} ${b.item_name_zh ?? ''}`.trim() || '—' },
    { header: '数量', align: 'right', render: (b) => <span className="num">{num(b.quantity, 3)}</span> },
    { header: '版本', render: (b) => <span className="muted">v{b.version}</span> },
    { header: '备注', render: (b) => <span className="muted">{b.notes || '—'}</span> },
    { header: '启用状态', render: (b) => <Pill tone={b.active ? 'ok' : 'neutral'} label={b.active ? '启用' : '停用'} /> },
    { header: '审批状态', render: (b) => <Pill tone={b.status === 'approved' ? 'ok' : 'warn'} label={b.status === 'approved' ? '已批准' : b.status === 'deactivated' ? '已停用' : '草稿 · 未批准'} /> },
  ];

  return (
    <>
      {creating ? (
        <NewBomForm onCancel={() => setCreating(false)} onCreated={() => { setCreating(false); setReloadSignal((n) => n + 1); }} />
      ) : null}
      <ListPage<Bom>
        title="BOM 物料清单"
        desc="物料清单与工艺路线 · /api/boms。展开查看用料/工序；工单关联 BOM 后可自动展开工序任务并按用量比例领料"
        endpoint="/api/boms"
        columns={columns}
        rowId={(b) => b.id}
        showYear={false}
        searchPlaceholder="搜索 BOM 编号 / 成品编码 / 名称…"
        reloadSignal={reloadSignal}
        initialExpandedId={openId}
        rowActions={(row) => <RecordActions type="boms" row={row} fields={RECORD_FIELDS['boms']} label={String(row.number)} onDone={() => setReloadSignal((n) => n + 1)} />}
        actions={canWrite ? (
          <button className="btn primary" onClick={() => setCreating((v) => !v)}>{creating ? '收起新建' : '新建 BOM'}</button>
        ) : undefined}
        renderDetail={(row) => <BomDetail id={row.id} canConvert={canWrite} onApproved={() => setReloadSignal((n) => n + 1)} />}
      />
      <QcReviewQueuePanel reloadSignal={reloadSignal} />
    </>
  );
}

/**
 * 质检审核队列（决策二 / T44 第 5 步）。
 *
 * 存在的理由：审核粒度是「默认 BOM → 工序 → 是否需检 → 检查点」，共 200 多道工序。
 * 靠质量自己在 320 张 BOM 里逐张开、逐行找，必然漏。队列把「还差哪些」直接算出来。
 * 统计口径直接取后端 summary，前端不自己数，避免两处口径打架。
 */
function QcReviewQueuePanel({ reloadSignal }: { reloadSignal: number }) {
  const { user } = useAuth();
  const mayView = can(user, 'qc-review', 'view');
  const [queue, setQueue] = useState<QcReviewQueue | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!mayView) return;
    let alive = true;
    setLoading(true);
    (async () => {
      try {
        const d = await api.detail<QcReviewQueue>('/api/qc-review/queue?limit=200');
        if (alive) { setQueue(d); setErr(null); }
      } catch (e) {
        if (alive) setErr(e instanceof ApiError ? e.message : '审核队列加载失败');
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [mayView, reloadSignal]);

  if (!mayView) return null;
  const s = queue?.summary;

  return (
    <section className="panel" style={{ marginTop: 'var(--gap)' }}>
      <div className="panel-head">
        <div>
          <h2>质检审核队列</h2>
          <div className="desc">工序「是否需检」的质量三态审核 · /api/qc-review/queue</div>
        </div>
      </div>
      <div className="panel-body">
        {err ? <div className="notice bad" style={{ marginBottom: 10 }}>{err}</div> : null}
        {loading && !queue ? <Spinner label="加载审核队列…" /> : null}
        {s ? (
          <>
            <div className="kv-grid" style={{ marginBottom: 10 }}>
              <KeyValue k="待审核工序">{num(s.ops_pending, 0)} / {num(s.ops_total, 0)}</KeyValue>
              <KeyValue k="已判定必检">{num(s.ops_required, 0)}</KeyValue>
              <KeyValue k="已确认免检">{num(s.ops_not_required, 0)}</KeyValue>
              <KeyValue k="必检但未绑检查点">{num(s.ops_required_no_checkpoint, 0)}</KeyValue>
              <KeyValue k="待审 BOM 数">{num(queue?.boms.length ?? 0, 0)}</KeyValue>
            </div>
            {queue?.scope_note ? <div className="notice" style={{ marginBottom: 10 }}>{queue.scope_note}</div> : null}
            {queue && queue.boms.length ? (
              <LineTable<QcReviewQueueBom>
                rows={queue.boms}
                emptyText="没有待审核的 BOM"
                columns={[
                  { header: 'BOM', render: (b) => <span className="num strong">{b.number}</span> },
                  { header: '成品', render: (b) => `${b.item_code ?? ''} ${b.item_name_zh ?? ''}`.trim() || '—' },
                  { header: '版本', render: (b) => <span className="muted">v{b.version}</span> },
                  { header: '默认', render: (b) => b.is_default ? <Pill tone="ok" label="默认" /> : <Pill tone="neutral" label="非默认" /> },
                  { header: '工序数', align: 'right', render: (b) => <span className="num">{num(b.operation_count, 0)}</span> },
                  { header: '待审核', align: 'right', render: (b) => b.pending_count
                    ? <Pill tone="warn" label={`${b.pending_count} 道`} />
                    : <span className="muted">—</span> },
                  { header: '必检未绑点', align: 'right', render: (b) => b.required_without_checkpoint
                    ? <Pill tone="bad" label={`${b.required_without_checkpoint} 道`} />
                    : <span className="muted">—</span> },
                  { header: '操作', render: (b) => <Link to={`/boms?open=${b.id}`}>去审核</Link> },
                ]}
              />
            ) : null}
          </>
        ) : null}
      </div>
    </section>
  );
}
